import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, MessageCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useT } from '../utils/i18n';
interface FaqItem {
  questionKey: string;
  answer: string | React.ReactNode;
  answerKey?: string;
}
const faqs: FaqItem[] = [{
  questionKey: 'faq.q1',
  answer: <>
        We deliver throughout King and Pierce counties — Bellevue, Kirkland,
        Redmond, Issaquah, Renton, Seattle, Auburn, Kent, Federal Way, Tacoma,
        Puyallup, Bonney Lake, and beyond.{' '}
        <Link to="/delivery-zones" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-757" data-mp="2042n8ogdx7k4">
          View our full delivery zone map →
        </Link>
      </>
}, {
  questionKey: 'faq.q2',
  answerKey: 'faq.a2',
  answer: ''
}, {
  questionKey: 'faq.q3',
  answerKey: 'faq.a3',
  answer: ''
}, {
  questionKey: 'faq.q4',
  answer: <>
        Use our{' '}
        <Link to="/help" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-758" data-mp="js5ngqceorfb">
          Material Calculator & Quiz
        </Link>{' '}
        to estimate based on your project dimensions, or{' '}
        <Link to="/compare" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-759" data-mp="1apsu2tf75j9a">
          compare materials
        </Link>{' '}
        side by side. You can also give us a call and we'll help you figure it
        out.
      </>
}, {
  questionKey: 'faq.q5',
  answerKey: 'faq.a5',
  answer: ''
}, {
  questionKey: 'faq.q6',
  answerKey: 'faq.a6',
  answer: ''
}, {
  questionKey: 'faq.q7',
  answer: <>
        It depends on the situation. In some cases we can spread the material
        depending on access, terrain, and the type of project.{' '}
        <a href="tel:206-619-3742" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-760" data-mp="wvt63opp2m09">
          Give us a call
        </a>{' '}
        to discuss your specific setup. Check out our{' '}
        <Link to="/delivery-prep" className="underline underline-offset-2 hover:text-white/70 transition-colors" data-id="element-761" data-mp="2pmdci2c23cra">
          Delivery Prep Guide
        </Link>{' '}
        to get your site ready.
      </>
}];
export function FAQ() {
  const t = useT();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [lastClosedIndex, setLastClosedIndex] = useState<number | null>(null);
  const toggleFAQ = (index: number) => {
    if (openIndex === index) {
      // Closing this question — mark it as last closed
      setLastClosedIndex(index);
      setOpenIndex(null);
    } else {
      // Opening a new question — clear last closed
      setLastClosedIndex(null);
      setOpenIndex(index);
    }
  };
  return <section className="py-16 sm:py-24 dark:bg-[#202020] transition-colors duration-300 scroll-mt-32 bg-[#fffff8]" id="faq" data-id="element-762" data-mp="2umhtdtgvm9i1">
      {/* Header */}
      <div className="mx-auto mb-12 max-w-5xl px-4 sm:mb-20 sm:px-6" data-id="element-763" data-mp="1n621caaefeed">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.4
      }} data-id="element-764" data-mp="1j5rxb3662y25">
          <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-baseline md:gap-7" data-id="element-765" data-mp="2qx982h3f0b1">
            <h2 className="font-heading text-5xl font-extrabold leading-[1.1] tracking-tight text-stone-900 dark:text-white md:text-6xl lg:text-7xl" data-id="element-766" data-mp="7o5onvauwq8l">
              Da <span className="font-black text-[#04A05C]" data-id="element-767" data-mp="hf0cv57nxzwo">FAQs</span>
            </h2>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#17A348]" data-id="element-768" data-mp="hhctnf1oq485">— Get answers fast</span>
          </div>
          <p className="max-w-2xl font-sub text-lg font-medium leading-relaxed tracking-wide text-brand-charcoal dark:text-white" data-id="element-769" data-mp="qf6oeljswpt7">
            {t('faq.subtitle')}
          </p>
        </motion.div>
      </div>

      {/* FAQ List - Full Width */}
      <div className="w-full" data-id="element-770" data-mp="2s8mg7lrmkapr">
        {faqs.map((faq, index) => {
        const isOpen = openIndex === index;
        const answerContent = faq.answerKey ? t(faq.answerKey) : faq.answer;
        return <motion.div key={index} initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.05,
          duration: 0.3
        }} className={`w-full transition-colors duration-300 ${isOpen ? 'bg-[#00754A]' : 'bg-transparent'}`} data-id="element-771" data-mp="21uojhbup542y" data-mpi={index}>
              <div className="max-w-5xl mx-auto px-4 sm:px-6" data-id="element-772" data-mp="26t83h6pqc5so">
                <button onClick={() => toggleFAQ(index)} className="w-full flex items-start justify-between gap-6 py-8 sm:py-12 text-left group" aria-expanded={isOpen} aria-controls={`faq-answer-${index}`} data-id="element-773" data-mp="1j69ral8n7k0k">
                  <span className={`font-heading text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight transition-colors duration-300 ${isOpen ? 'text-white' : lastClosedIndex === index ? 'text-[#04A05C] dark:text-[#04A05C] group-hover:text-[#048036] dark:group-hover:text-[#048036]' : 'text-stone-900 dark:text-white group-hover:text-[#04A05C] dark:group-hover:text-[#04A05C]'}`} data-id="element-774" data-mp="1swuk1s1mxubk">
                    {t(faq.questionKey)}
                  </span>
                  <motion.span animate={{
                rotate: isOpen ? 45 : 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className={`shrink-0 mt-1 sm:mt-2 transition-colors duration-300 ${isOpen ? 'text-white' : 'text-stone-900 dark:text-white'}`} data-id="element-775" data-mp="3buhginx2m4fl">
                    <Plus className="w-8 h-8 sm:w-12 sm:h-12 stroke-[1]" data-id="element-776" data-mp="3pth4rugo8hb8" />
                  </motion.span>
                </button>

                <AnimatePresence data-id="element-777" data-mp="3usbnkwen25vc">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" id={`faq-answer-${index}`} data-id="element-778" data-mp="3gtc6c9tggt97">
                      <p className="pb-12 sm:pb-16 text-lg sm:text-xl md:text-2xl text-white/95 leading-relaxed max-w-4xl font-medium" data-id="element-779" data-mp="fxb7ed7haqi1">
                        {answerContent}
                      </p>
                    </motion.div>}
                </AnimatePresence>
              </div>
            </motion.div>;
      })}
      </div>

      {/* Still Have Questions */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-8 sm:mt-10" data-id="element-780" data-mp="15ssyju9p40s">
        <div className="flex flex-col sm:flex-row items-center justify-center text-center sm:text-left py-4 gap-3 sm:gap-5" data-id="element-781" data-mp="3tcg0dov58lav">
          <MessageCircle className="w-5 h-5 text-[#04A05C] shrink-0" data-id="element-782" data-mp="3oeo6wqeuor4k" />
          <p className="text-stone-900 dark:text-white font-heading font-extrabold text-lg sm:text-xl" data-id="element-783" data-mp="3je9z99h5vj72">
            {t('faq.stillQuestions')}
            <span className="font-normal text-stone-600 dark:text-stone-400 text-base ml-2" data-id="element-784" data-mp="1hm57pz3kqnwv">
              {t('faq.shootText')}
            </span>
          </p>
          <a href="sms:206-619-3742" className="inline-flex items-center justify-center px-6 py-2.5 hover:bg-[#048B3A] text-white rounded-full font-bold text-sm transition-colors bg-[#04A05C]" data-id="element-785" data-mp="1cnk8a1w5c89h">
            {t('faq.textUs')}
          </a>
        </div>
      </div>
    </section>;
}